import { api } from './client';
import type {
  Contract, ContractListItem, ContractEvent, Counterparty,
  Construct, SignatureInfo, Paginated,
} from '@/types';

export interface ContractFilters {
  type?: string; status?: string; object?: number;
  counterparty?: number; search?: string; ordering?: string; page?: number;
}

export const contractsApi = {
  async list(filters: ContractFilters = {}): Promise<Paginated<ContractListItem>> {
    const { data } = await api.get<Paginated<ContractListItem>>('/contracts/', { params: filters });
    return data;
  },
  async get(id: number): Promise<Contract> {
    const { data } = await api.get<Contract>(`/contracts/${id}/`);
    return data;
  },
  async create(payload: Partial<Contract>): Promise<Contract> {
    const { data } = await api.post<Contract>('/contracts/', payload);
    return data;
  },
  async update(id: number, payload: Partial<Contract>): Promise<Contract> {
    const { data } = await api.patch<Contract>(`/contracts/${id}/`, payload);
    return data;
  },
  async remove(id: number): Promise<void> { await api.delete(`/contracts/${id}/`); },
  async changeStatus(id: number, status: string, note = ''): Promise<Contract> {
    const { data } = await api.post<Contract>(`/contracts/${id}/change_status/`, { status, note });
    return data;
  },
  async uploadSigned(id: number, file: File): Promise<SignatureInfo> {
    const form = new FormData();
    form.append('file', file);
    const { data } = await api.post<SignatureInfo>(`/contracts/${id}/upload_signed/`, form,
      { headers: { 'Content-Type': 'multipart/form-data' } });
    return data;
  },
  async confirmSignature(id: number): Promise<Contract> {
    const { data } = await api.post<Contract>(`/contracts/${id}/confirm_signature/`);
    return data;
  },
  async constructs(id: number): Promise<(Construct & { is_taken: boolean })[]> {
    const { data } = await api.get(`/contracts/${id}/constructs/`);
    return data;
  },
  async subcontracts(id: number): Promise<ContractListItem[]> {
    const { data } = await api.get(`/contracts/${id}/subcontracts/`);
    return data;
  },
  async createSubcontract(id: number, payload: Partial<Contract>): Promise<Contract> {
    const { data } = await api.post(`/contracts/${id}/subcontracts/`, payload);
    return data;
  },
  async timeline(id: number): Promise<ContractEvent[]> {
    const { data } = await api.get<ContractEvent[]>(`/contracts/${id}/timeline/`);
    return data;
  },
};

export const counterpartiesApi = {
  async list(kind?: string): Promise<Paginated<Counterparty>> {
    const { data } = await api.get<Paginated<Counterparty>>('/counterparties/', { params: kind ? { kind } : {} });
    return data;
  },
  async create(payload: Partial<Counterparty>): Promise<Counterparty> {
    const { data } = await api.post<Counterparty>('/counterparties/', payload);
    return data;
  },
  async checkFocus(id: number): Promise<Record<string, unknown>> {
    const { data } = await api.post(`/counterparties/${id}/check_focus/`);
    return data;
  },
};
